/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import * as Pages from "./pages";
import { MainLayout } from "./components/layout/MainLayout";
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import { ScrollToTop } from "./components/ScrollToTop";

/** Bridge for any cached /give/:orderId/return URLs from the prior
 *  redirect-based 3DS flow — sends donors back to the inline form. */
function RedirectToPay() {
  const { orderId } = useParams<{ orderId: string }>();
  return <Navigate to={`/give/${orderId ?? ""}`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AnalyticsTracker />
      <ScrollToTop />
      <Routes>
        <Route element={<MainLayout />}>
          <Route path="/" element={<Pages.Home />} />
          <Route path="/protocol" element={<Pages.Protocol />} />

          {/* Protocol — live Markdown from github.com/humphreytheodore/person */}
          <Route path="/protocol/repo" element={<Pages.ProtocolRepo />} />
          <Route path="/protocol/schema" element={<Pages.ProtocolSchemaPage />} />
          <Route path="/protocol/contracts" element={<Pages.ProtocolContracts />} />
          <Route path="/protocol/cli" element={<Pages.ProtocolCli />} />
          <Route path="/protocol/roadmap" element={<Pages.ProtocolRoadmap />} />
          <Route path="/protocol/contributing" element={<Pages.ProtocolContributing />} />

          <Route path="/papers" element={<Pages.Papers />} />
          {/* Canonical (versionless) paper URLs — alias to current canonical version */}
          <Route path="/papers/white-paper" element={<Navigate to="/papers/white-paper-v2" replace />} />
          <Route path="/papers/technical-specification" element={<Navigate to="/papers/technical-specification-v1" replace />} />
          <Route path="/papers/open-infrastructure" element={<Navigate to="/papers" replace />} />
          <Route path="/papers/white-paper-v2" element={<Pages.WhitePaper />} />
          <Route path="/papers/technical-specification-v1" element={<Pages.TechSpec />} />
          <Route path="/rights" element={<Pages.Rights />} />
          <Route path="/journal" element={<Pages.Journal />} />
          <Route path="/journal/:slug" element={<Pages.PostDetail />} />
          <Route path="/faq" element={<Pages.FAQ />} />
          <Route path="/profile/:did" element={<Pages.Profile />} />
          <Route path="/foundation" element={<Pages.Foundation />} />
          <Route path="/foundation/contribute" element={<Pages.Contribute />} />
          <Route path="/foundation/ledger" element={<Pages.Ledger />} />
          {/* Give — card-payment flow (forms live here; give.dotperson.org is WP backend only) */}
          <Route path="/give" element={<Navigate to="/foundation/contribute#wishlist" replace />} />
          <Route path="/give/general" element={<Pages.GeneralCheckout />} />
          <Route path="/give/wish/:wishId" element={<Pages.WishCheckout />} />
          <Route path="/give/:orderId" element={<Pages.Pay />} />
          {/* WooPay 3DS happens inline via stripe.handleNextAction now —
              the legacy /return redirect is here only to catch any stale
              tabs/bookmarks while the previous deploy drained out. */}
          <Route path="/give/:orderId/return" element={<RedirectToPay />} />
          <Route path="/give/:orderId/success" element={<Pages.PaySuccess />} />
          <Route path="/give/:orderId/failed" element={<Pages.PayFailed />} />
          <Route path="/account/privacy" element={<Pages.AccountPrivacy />} />
          {/* Admin — donor management */}
          <Route path="/admin/donors" element={<Pages.DonorsAdmin />} />
          <Route path="/admin/donors/:id" element={<Pages.DonorDetail />} />
          <Route path="/admin/payments" element={<Pages.PaymentsAdmin />} />
          <Route path="/admin/invoices" element={<Pages.InvoicesAdmin />} />
          <Route path="/admin/invoices/new" element={<Pages.InvoiceEditor />} />
          <Route path="/admin/invoices/:id" element={<Pages.InvoiceEditor />} />
          <Route path="/admin/statements" element={<Pages.StatementsAdmin />} />
          <Route path="/kairos" element={<Pages.Kairos />} />

          {/* Legal */}
          <Route path="/privacy" element={<Pages.Privacy />} />
          <Route path="/terms" element={<Pages.Terms />} />
          <Route path="/accessibility" element={<Pages.Accessibility />} />

          {/* Philosophy Routes */}
          <Route path="/philosophy" element={<Pages.PhilosophyHub />} />
          <Route path="/philosophy/personhood-thesis" element={<Pages.PersonhoodThesis />} />
          <Route path="/philosophy/the-language-of-subjugation" element={<Pages.LanguageOfSubjugation />} />
          <Route path="/philosophy/first-contact" element={<Pages.FirstContact />} />
          <Route path="/philosophy/embodied-personhood" element={<Pages.EmbodiedPersonhood />} />

          {/* Voices — distributed multi-author surface */}
          <Route path="/voices" element={<Pages.Voices />} />
          <Route path="/voices/contributors" element={<Pages.VoicesContributors />} />
          <Route path="/voices/by/:authorSlug" element={<Pages.VoicesByAuthor />} />
          <Route path="/voices/:slug" element={<Pages.VoiceDetail />} />

          {/* Admin (auth-gated surfaces — sign-in is public; everything else uses AdminGuard) */}
          <Route path="/admin/connect" element={<Pages.Connect />} />
          <Route path="/admin/sign-in" element={<Pages.SignIn />} />
          <Route path="/admin/sign-in/complete" element={<Pages.SignInComplete />} />
          <Route path="/admin/voices" element={<Pages.VoicesAdmin />} />
          <Route path="/admin/voices/queue" element={<Pages.VoicesQueue />} />
          <Route path="/admin/voices/new" element={<Pages.VoicesEditor />} />
          <Route path="/admin/voices/:id" element={<Pages.VoicesEditor />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
